/*eslint-disable react-perf/jsx-no-jsx-as-prop */
import React from 'react';
import styled from 'styled-components';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { ThemeProvider } from 'styled-components';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { SnackbarProvider, type SnackbarOrigin } from 'notistack';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { PAYPAL_CLIENT_ID, PAYPAL_CURRENCY } from './consts';

import client from './utils/client';
import { useCreateTheme } from './utils/theme';
import PortalProvider from './components/PortalProvider';
import Footer from './components/Footer';
import ContentWrapper from './components/ContentWrapper';
import Txt from './components/Txt';
import Logo from './components/Logo';
import GenericAppBar2 from './components/GenericAppBar2';

import { useAppState } from './utils/app';
import {
	useCurrentUser,
	usePotentiallyRequestPasswordReset,
	usePotentiallyRequestTermsAcceptance,
	useUserType,
} from './utils/user';
import { useMaintenanceInfo } from './utils/statistics';
import { CookieProvider, usePotentiallyCookieBanner } from './utils/cookie';
import YoutubeIframeView from './screens/YoutubeIframe';

import {
	usePotentiallyHandleLeavePlayerView,
	usePotentiallyScrollToTop,
	useTrackPageViews,
	usePotentiallyNavigateToAdminRoute,
} from './utils/hooks';

import getRoutes from './routes';

const anchorOrign = {
	horizontal: 'center',
	vertical: 'bottom',
} as SnackbarOrigin;

const MainScreen = styled(Box)`
	min-height: 100vh;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Navigator = () => {
	const appState = useAppState();
	const maintenanceInfo = useMaintenanceInfo();
	// const currentUser = useCurrentUser();
	// console.log('currentUser: ', currentUser);
	const userType = useUserType();
	usePotentiallyRequestPasswordReset();
	usePotentiallyRequestTermsAcceptance();
	usePotentiallyCookieBanner();
	usePotentiallyScrollToTop();
	usePotentiallyHandleLeavePlayerView();
	usePotentiallyNavigateToAdminRoute();
	useTrackPageViews();

	const routes = React.useMemo(() => getRoutes(userType), [userType]);

	return (
		<MainScreen>
			<CssBaseline />
			{maintenanceInfo?.isMaintained ? (
				<ContentWrapper center minHeight="10rem">
					<Logo m="0 0 2rem 0" />
					<Txt variant="h6">{maintenanceInfo.title || 'Wartungsarbeiten'} </Txt>
					<Txt>
						{maintenanceInfo?.description ||
							'Es finden aktuell Wartungsarbeiten statt. Bitte versuche es zu einem späteren Zeitpunkt wieder.'}
					</Txt>
				</ContentWrapper>
			) : appState === 'LOADING' ? (
				<CircularProgress size={24} />
			) : (
				<>
					<GenericAppBar2 />
					<Routes>
						{routes.map(({ accessibility, path, element }) =>
							accessibility.includes(appState) ? (
								<Route key={path} path={path} element={element} />
							) : null
						)}
					</Routes>
					{userType === 'ADMIN' ? null : userType === 'ACCESS_PHRASE_USER' ? (
						<Footer isAccessPhraseUser={true} />
					) : (
						<Footer />
					)}
				</>
			)}
		</MainScreen>
	);
};

const initialPaypalOptions = {
	clientId: PAYPAL_CLIENT_ID,
	currency: PAYPAL_CURRENCY,
	intent: 'capture',
};

const App = () => {
	const theme = useCreateTheme();
	const location = useLocation();
	if (location.pathname === '/ytplayer') {
		return (
			<ThemeProvider theme={theme}>
				<CookieProvider>
					<PortalProvider>
						<YoutubeIframeView />
					</PortalProvider>
				</CookieProvider>
			</ThemeProvider>
		);
	}
	return (
		<PayPalScriptProvider options={initialPaypalOptions}>
			<ApolloProvider client={client}>
				<ThemeProvider theme={theme}>
					<CookieProvider>
						<SnackbarProvider maxSnack={3} anchorOrigin={anchorOrign}>
							<PortalProvider>
								<Navigator />
							</PortalProvider>
						</SnackbarProvider>
					</CookieProvider>
				</ThemeProvider>
			</ApolloProvider>
		</PayPalScriptProvider>
	);
};

export default App;
